import type { ViewerShell } from '../shell';
import { csTools, type ToolStore } from '../tools/cstypes';
import { currentLeftTool, onLeftToolChange, setLeftTool, type ToolId } from '../tools';

/**
 * " Bloquear herram. " / " Desbloquear herram." (spec section 2, More menu; state in
 * localStorage["tools-locked"]). Locked: an annotation tool stays on the left button after a drawing
 * is finished. Unlocked: once the drawing is finished the left button goes back to the tool used
 * before it (window/level by default).
 *
 * Same storage values and default as IMEXHS: "locked" / "unlocked", and unlocked when nothing is stored.
 */

export const LOCK_TEXT = { lock: ' Bloquear herram. ', unlock: ' Desbloquear herram.' } as const;
export const LOCK_TITLE = { lock: 'Bloquear herramientas de anotación', unlock: 'Desbloquear herramientas de anotación' } as const;
const STORAGE_KEY = 'tools-locked';
const DEFAULT_LOCKED = false;
const STORED = { locked: 'locked', unlocked: 'unlocked' } as const;
const LEFT_BUTTON = 0;

/** Mouse-ups a drawing takes, after the one that created it (two-line tools need a second stroke). */
const STROKES: Partial<Record<ToolId, number>> = {
  lines: 1, probe: 1, ellipticalRoi: 1, rectangleRoi: 1, Bidirectional: 1, arrowAnnotate: 1, text: 1,
  angles: 2, cobbs: 2, salvati: 2, parallelDistance: 2, cardiothoracicIndex: 2, coxometry: 2, cobbAngleMV: 2,
};
// Polygon ROI (open-ended clicks) and spine labels (a running sequence) never revert on their own.

let locked = DEFAULT_LOCKED;
/** Tool to go back to when a drawing finishes while unlocked. */
let restoreTool: ToolId = 'window-level';
let pending: { tool: ToolId; strokesLeft: number } | null = null;
const listeners = new Set<(locked: boolean) => void>();

function readStored(): boolean {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === null ? DEFAULT_LOCKED : value === STORED.locked;
  } catch {
    return DEFAULT_LOCKED;
  }
}

function store(value: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? STORED.locked : STORED.unlocked);
  } catch (error) {
    console.warn('[viewer] tools-locked not persisted', error);
  }
}

export function areToolsLocked(): boolean {
  return locked;
}

export function setToolsLocked(value: boolean): void {
  locked = value;
  pending = null;
  store(value);
  listeners.forEach((listener) => listener(value));
}

export function toggleToolsLock(): void {
  setToolsLocked(!locked);
}

/** The More-menu label for the current state. */
export function lockToolsLabel(): { label: string; title: string } {
  return locked ? { label: LOCK_TEXT.unlock, title: LOCK_TITLE.unlock } : { label: LOCK_TEXT.lock, title: LOCK_TITLE.lock };
}

export function onToolsLockChange(listener: (locked: boolean) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function toolIsDrawing(): boolean {
  return (csTools() as unknown as { store?: ToolStore }).store?.state.isToolLocked === true;
}

function onMeasurementAdded(): void {
  const tool = currentLeftTool();
  const strokes = STROKES[tool];
  if (locked || !strokes || pending) return;
  pending = { tool, strokesLeft: strokes };
}

/** After the mouse-up that ends a drawing (cornerstone-tools has handled it), hand the button back. */
function onMouseUp(event: MouseEvent): void {
  if (event.button !== LEFT_BUTTON || !pending) return;
  setTimeout(() => {
    if (!pending || locked) return;
    if (currentLeftTool() !== pending.tool) {
      pending = null;
      return;
    }
    if (toolIsDrawing()) return;
    pending.strokesLeft--;
    if (pending.strokesLeft > 0) return;
    pending = null;
    setLeftTool(restoreTool);
  }, 0);
}

function watchViewports(shell: ViewerShell): void {
  const added = csTools().EVENTS.MEASUREMENT_ADDED;
  shell.viewports().forEach((viewport) => viewport.element.addEventListener(added, onMeasurementAdded));
}

export function install(shell: ViewerShell): void {
  locked = readStored();
  onLeftToolChange((id) => {
    if (!STROKES[id] && id !== 'freehandMouseTool' && id !== 'textMarker') restoreTool = id;
    if (pending && pending.tool !== id) pending = null;
  });
  shell.on('layout-changed', () => {
    pending = null;
    watchViewports(shell);
  });
  window.addEventListener('mouseup', onMouseUp);
}
